<template>
  <div class="sifcop-container">
    <div class="headerBlock">
      <h1 class="primaryFont title">Nueva Acta</h1>
      <p class="secondaryFont subtitle">Formulario de digitalización SIFCOP – Convenio Policía</p>
    </div>
    <div class="actionsRow">
      <Button
        v-if="sifcopStore.isCreate"
        class="secondaryButton button flex-grow-0"
        :disabled="sifcopStore.isLoading"
        @click="handleCancel"
      >
        <div class="flex align-items-center w-full gap-2 justify-content-start">
          <X style="width: 20px; height: 20px" />
          <span class="ml-2">Cancelar</span>
        </div>
      </Button>

      <Button
        v-if="sifcopStore.isCreate"
        class="button primaryButton flex-grow-0"
        :disabled="sifcopStore.isLoading || !sifcopStore.isFormValid"
        @click="handleSave"
      >
        <div
          v-if="!sifcopStore.isLoading"
          class="flex align-items-center gap-2 justify-content-center"
        >
          <Check style="width: 20px; height: 20px" />
          <span>Guardar</span>
        </div>
        <div v-else class="flex align-items-center gap-2 justify-content-center">
          <i class="pi pi-spin pi-spinner mr-2" style="font-size: 1rem" />
          <span>Guardando...</span>
        </div>
      </Button>
    </div>

    <SifcopForm />

    <CustomModal
      ref="modalRef"
      :modalType="modalData.modalType"
      :title="modalData.title"
      :message="modalData.message"
      :showSecondaryButton="modalData.showSecondaryButton"
      :buttonLabels="modalData.buttonLabels"
      @confirm="modalData.onConfirm"
      @cancel="modalData.onCancel"
    />
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouterCompat } from "@/composables";
import SifcopForm from "@/components/forms/SifcopForm.vue";
import CustomModal from "@/components/CustomModal.vue";
import { useBreadcrumbStore } from "@/store/Pinia/useBreadcrumbStore";
import { useSifcopStore } from "@/store/Pinia/useSifcopStore";
import { Check, X } from "lucide-vue";

const breadcrumbStore = useBreadcrumbStore();
const sifcopStore = useSifcopStore();
const { route, router } = useRouterCompat();
const modalRef = ref(null);
const modalData = ref({
  title: "No se pudo guardar el acta",
  message: "",
  modalType: "info",
  showSecondaryButton: false,
  buttonLabels: ["Aceptar"],
  onConfirm: () => modalRef.value?.close(),
  onCancel: () => modalRef.value?.close(),
});

const showModal = (config) => {
  modalData.value = {
    ...modalData.value,
    title: config.title,
    message: config.message,
    modalType: config.modalType,
    showSecondaryButton: config.showSecondaryButton ?? false,
    buttonLabels: config.buttonLabels,
    onConfirm: config.onConfirm ?? (() => modalRef.value?.close()),
    onCancel: config.onCancel ?? (() => modalRef.value?.close()),
  };

  modalRef.value?.open();
};

const handleCancel = () => {
  router.push("/sifcop");
};

const handleSave = async () => {
  const result = await sifcopStore.handleCreate();
  if (!result?.ok) {
    showModal({
      title: "No se pudo guardar el acta",
      message: "Ocurrió un error al crear el acta SIFCOP.",
      modalType: "error",
      buttonLabels: ["Aceptar"],
    });
    return;
  }

  showModal({
    title: "Acta registrada",
    message: "El acta SIFCOP se guardó correctamente.",
    modalType: "success",
    buttonLabels: ["Continuar"],
    onConfirm: () => {
      modalRef.value?.close();
      sifcopStore.startCreate();
      router.push("/sifcop");
    },
  });
};

onMounted(() => {
  sifcopStore.startCreate();

  breadcrumbStore.setItems([
    { label: "SIFCOP", to: "/sifcop" },
    { label: "Nuevo formulario", to: route.fullPath },
  ]);
});
</script>

<style scoped>
.sifcop-container {
  width: 100%;
  padding: 0 2rem 2rem;
}

.headerBlock {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.title {
  font-weight: 700;
  font-size: 28px;
  line-height: 32px;
  color: #000000;
  margin: 0 0 0.5rem;
}

.subtitle {
  font-size: 16px;
  line-height: 24px;
  color: #4b5563;
  margin: 0;
}

.actionsRow {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.button {
  min-width: 130px;
  min-height: 43px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  border: none;
  border-radius: 6px;
}

.primaryButton {
  color: white;
  background-color: #0b5195;
}

.primaryButton:hover {
  background-color: #1366b0;
}

.secondaryButton {
  background-color: white;
  color: #0b5195;
  border: 1px #0b5195 solid;
}

.secondaryButton:hover {
  background-color: rgba(255, 255, 255, 0.267);
  color: #1366b0;
}

@media (max-width: 992px) {
  .sifcop-container {
    padding: 0 0 2rem;
  }
}
</style>
